/**
 * PDFExportService
 * 
 * Handles PDF generation from HTML content using html2pdf.js
 * Note: html2pdf.js must be added to package.json dependencies
 */

/**
 * Default PDF options
 */
const DEFAULT_OPTIONS = {
  margin: [0.5, 0.5, 0.5, 0.5], // top, right, bottom, left in inches
  filename: 'succession-plan.pdf',
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: { 
    scale: 2,
    useCORS: true,
    letterRendering: true
  },
  jsPDF: { 
    unit: 'in', 
    format: 'letter', 
    orientation: 'portrait' 
  },
  pagebreak: { 
    mode: ['avoid-all', 'css', 'legacy'] 
  }
};

/**
 * Generate PDF from HTML element
 * 
 * @param {string} elementId - ID of the HTML element to convert
 * @param {string} filename - Output filename (without extension)
 * @param {Object} options - Override default PDF options
 * @returns {Promise} - Resolves when PDF is downloaded
 */
export const exportToPDF = async (elementId, filename = 'succession-plan', options = {}) => {
  const element = document.getElementById(elementId);
  
  if (!element) {
    throw new Error(`Element with ID "${elementId}" not found`);
  }
  
  // Dynamically import html2pdf to avoid SSR issues
  const html2pdf = (await import('html2pdf.js')).default;
  
  const mergedOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
    filename: `${filename}-${Date.now()}.pdf`
  };
  
  return html2pdf()
    .set(mergedOptions)
    .from(element)
    .save();
};

/**
 * Generate PDF blob (for preview or programmatic use)
 * 
 * @param {string} elementId - ID of the HTML element to convert
 * @param {Object} options - Override default PDF options
 * @returns {Promise<Blob>} - PDF as Blob
 */
export const generatePDFBlob = async (elementId, options = {}) => {
  const element = document.getElementById(elementId);
  
  if (!element) {
    throw new Error(`Element with ID "${elementId}" not found`);
  }
  
  const html2pdf = (await import('html2pdf.js')).default;
  
  const mergedOptions = {
    ...DEFAULT_OPTIONS,
    ...options
  };
  
  return html2pdf()
    .set(mergedOptions)
    .from(element)
    .outputPdf('blob');
};

/**
 * Print-friendly export (opens print dialog)
 */
export const printDocument = () => {
  window.print();
};

/**
 * Export document as markdown file
 * 
 * @param {string} markdown - Markdown content
 * @param {string} filename - Output filename (without extension)
 */
export const exportToMarkdown = (markdown, filename = 'succession-plan') => {
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${Date.now()}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Check if html2pdf is available
 * 
 * @returns {Promise<boolean>}
 */
export const isPDFExportAvailable = async () => {
  try {
    await import('html2pdf.js');
    return true;
  } catch {
    console.warn('html2pdf.js is not available. PDF export will not work.');
    return false;
  }
};

/**
 * Export HTML element to PDF file
 * 
 * @param {HTMLElement} element - HTML element to convert
 * @param {string} filename - Output filename with extension
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const exportHtmlToPdf = async (element, filename = 'document.pdf') => {
  if (!element) {
    return { success: false, error: 'No element provided' };
  }
  
  try {
    const html2pdf = (await import('html2pdf.js')).default;
    
    await html2pdf()
      .set({
        ...DEFAULT_OPTIONS,
        filename
      })
      .from(element)
      .save();
    
    return { success: true };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export markdown content to file
 * 
 * @param {string} content - Markdown content
 * @param {string} filename - Output filename with extension
 * @returns {{success: boolean, error?: string}}
 */
export const exportMarkdownToFile = (content, filename = 'document.md') => {
  try {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting markdown:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export multiple files as a ZIP archive
 * Requires JSZip library
 * 
 * @param {Array<{name: string, content: string}>} files - Array of file objects
 * @param {string} zipFilename - Output ZIP filename
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const exportAsZip = async (files, zipFilename = 'succession-plan-documents.zip') => {
  try {
    // Dynamically import JSZip
    const JSZip = (await import('jszip')).default;
    
    const zip = new JSZip();
    
    // Add each file to the ZIP
    files.forEach(file => {
      zip.file(file.name, file.content);
    });
    
    // Generate the ZIP file
    const blob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });
    
    // Download the ZIP
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = zipFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Error creating ZIP:', error);
    
    // Fallback: if JSZip not available, download files individually
    if (error.message?.includes('Cannot find module') || error.message?.includes('jszip')) {
      console.warn('JSZip not available. Downloading files individually.');
      files.forEach(file => {
        exportMarkdownToFile(file.content, file.name);
      });
      return { success: true, fallback: true };
    }
    
    return { success: false, error: error.message };
  }
};

/**
 * Export both Assessment Report and Plan Document
 * 
 * @param {string} assessmentContent - Assessment report markdown
 * @param {string} planContent - Plan document markdown
 * @param {string} baseName - Base name for files (without extension)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const exportBothDocuments = async (assessmentContent, planContent, baseName = 'Succession_Plan') => {
  const files = [
    { name: `${baseName}_Assessment.md`, content: assessmentContent },
    { name: `${baseName}_Document.md`, content: planContent }
  ];
  
  return exportAsZip(files, `${baseName}_Complete.zip`);
};

/**
 * PDFExportService object for convenient import
 */
export const PDFExportService = {
  exportToPDF,
  generatePDFBlob,
  printDocument,
  exportToMarkdown,
  isPDFExportAvailable,
  exportHtmlToPdf,
  exportMarkdownToFile,
  exportAsZip,
  exportBothDocuments
};

